// components/recipes/workflow/Chip.tsx
//
// Attio-style editable chip. Renders as an inline pill that reads like a word
// in a sentence; clicking it reveals a small anchored popover (combobox, day
// toggles, numeric editor, …). No modal, no page dim.
//
// Visual system: neutral surfaces, one blue accent, soft borders, 13px text,
// optional leading status dot. Three intents cover the sentence-builder needs:
//   - 'default'  the verb/object chips ("Decrease bid")
//   - 'value'    the editable value chips ("by 10%") — accent-tinted
//   - 'ghost'    presets / low-emphasis affordances
import React from 'react'
import classNames from 'classnames'
import { Popover, Classes } from '../../ui/popover'

type ChipIntent = 'default' | 'value' | 'ghost'

interface ChipProps {
    children: React.ReactNode
    /** Popover body. When provided the chip becomes a click-to-edit control. */
    content?: React.ReactNode
    intent?: ChipIntent
    /** Show a leading status dot (e.g. enabled/active). */
    dot?: boolean | string // pass a tailwind color class for a custom dot
    /** Show the trailing caret that signals "this opens a menu". */
    caret?: boolean
    active?: boolean
    disabled?: boolean
    className?: string
    onClick?: () => void
    title?: string
}

const intentClasses: Record<ChipIntent, string> = {
    default: 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-gray-300',
    value: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300',
    ghost: 'bg-transparent border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700',
}

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(function Chip(
    {
        children,
        content,
        intent = 'default',
        dot,
        caret,
        active,
        disabled,
        className,
        onClick,
        title,
    },
    ref,
) {
    const dotColor = typeof dot === 'string' ? dot : 'bg-emerald-500'

    const chip = (
        <button
            ref={ref}
            type="button"
            disabled={disabled}
            title={title}
            onClick={onClick}
            className={classNames(
                'group inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-[3px]',
                'text-[13px] font-medium leading-5 transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60',
                intentClasses[intent],
                {
                    'ring-2 ring-blue-400/60': active,
                    'cursor-not-allowed opacity-50': disabled,
                    'cursor-pointer': !disabled,
                },
                className,
            )}
        >
            {dot && <span className={classNames('h-1.5 w-1.5 shrink-0 rounded-full', dotColor)} />}
            <span className="inline-flex items-center gap-1 whitespace-nowrap">{children}</span>
            {caret && (
                <svg
                    className="h-3 w-3 shrink-0 text-gray-400 transition-transform duration-150 group-hover:text-gray-600"
                    viewBox="0 0 12 12"
                    fill="none"
                >
                    <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </button>
    )

    if (!content || disabled) {
        return chip
    }

    return (
        <Popover content={content} placement="bottom-start" minimal interactionKind="click">
            {chip}
        </Popover>
    )
})

/** Small section wrapper used inside chip popovers. */
export const ChipPopover = ({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) => (
    <div
        className={classNames(
            'min-w-[200px] rounded-lg border border-gray-200 bg-white p-2 shadow-lg',
            className,
        )}
    >
        {children}
    </div>
)

/** A selectable row inside a chip popover combobox. */
export const ChipOption = ({
    label,
    description,
    selected,
    onClick,
    disabled,
    /** Close the popover after selecting. True for single-shot comboboxes; pass
     * false where the popover hosts more controls the user may still touch. */
    dismiss = true,
}: {
    label: React.ReactNode
    description?: React.ReactNode
    selected?: boolean
    onClick?: () => void
    disabled?: boolean
    dismiss?: boolean
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={classNames(
            'flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left',
            'text-[13px] transition-colors',
            disabled
                ? 'cursor-not-allowed text-gray-300'
                : classNames('hover:bg-gray-100', selected ? 'text-gray-900' : 'text-gray-700'),
            !disabled && dismiss && Classes.POPOVER_DISMISS,
        )}
    >
        <span className="flex flex-col">
            <span className="font-medium">{label}</span>
            {description && <span className="text-xs text-gray-400">{description}</span>}
        </span>
        {selected && (
            <svg className="h-4 w-4 shrink-0 text-blue-600" viewBox="0 0 16 16" fill="none">
                <path
                    d="M13 4.5L6.5 11L3 7.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        )}
    </button>
)
